/**
 * Forecast Events API
 *
 * API layer for forecast events (audit trail + manual notes)
 */

// @ts-ignore - JavaScript module without types
import { api } from '../../../utils/api';
// @ts-ignore - JavaScript module without types
import API_ENDPOINTS from '../../../config/apiConfig';

// ============ Event Types ============

export type ForecastEventType =
  | 'DRIVER_CHANGED'
  | 'OVERRIDE_CREATED'
  | 'OVERRIDE_UPDATED'
  | 'OVERRIDE_CLEARED'
  | 'ADJUSTMENT_CREATED'
  | 'ADJUSTMENT_UPDATED'
  | 'ADJUSTMENT_DELETED'
  | 'DAILY_VALUES_CREATED'
  | 'DAILY_VALUES_UPDATED'
  | 'DAILY_VALUES_CLEARED'
  | 'DAILY_VALUES_SPREAD'
  | 'DEPARTMENT_DRIVER_SET'
  | 'DEPARTMENT_DRIVER_UPDATED'
  | 'DEPARTMENT_DRIVER_CLEARED'
  | 'NOTE';

export interface ForecastEventData {
  // DRIVER_CHANGED
  previousDriver?: string | null;
  newDriver?: string | null;
  previousInput?: number | null;
  newInput?: number | null;

  // OVERRIDE_*
  previousAmount?: number | null;
  newAmount?: number | null;

  // ADJUSTMENT_*
  adjustmentId?: number;
  adjustmentType?: 'FLAT' | 'PERCENT';
  adjustmentValue?: number;
  previousType?: 'FLAT' | 'PERCENT';
  previousValue?: number;
  previousDescription?: string | null;
  description?: string | null;
  startMonth?: string;
  endMonth?: string;

  // NOTE
  content?: string;

  // DAILY_VALUES_*
  dailyValuesCount?: number;
  dailyValuesTotal?: number;
  spreadPattern?: 'manual' | 'even' | 'weekend' | 'ly' | 'otb';
  previousDailyTotal?: number | null;

  // DEPARTMENT_DRIVER_*
  departmentName?: string;
  affectedAccounts?: number;
  accountType?: 'Revenue' | 'Expense';
  cascadeOptions?: {
    clearAccountDrivers?: boolean;
    clearAccountOverrides?: boolean;
    clearAccountAdjustments?: boolean;
    clearAccountNotes?: boolean;
  };
}

export interface ForecastEventUiContext {
  tab?: string;
  panelTab?: string;
}

export interface ForecastEvent {
  id: string;
  orgId: number;
  type: ForecastEventType;
  locationId: number;
  versionId: number;
  accountId: number | null;
  departmentName: string | null;  // For department-level events
  year: number | null;
  month: number | null;
  data: ForecastEventData;
  uiContext: ForecastEventUiContext;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  isDeleted?: boolean;
}

// ============ Request Types ============

export interface GetEventsParams {
  orgId: number;
  locationId?: number;
  versionId?: number;
  accountId?: number;
  departmentName?: string;  // For department-level events
  year?: number;
  month?: number;
  types?: ForecastEventType[];
  limit?: number;
  offset?: number;
}

export interface CreateEventParams {
  type: ForecastEventType;
  orgId: number;
  locationId: number;
  versionId: number;
  accountId?: number;
  departmentName?: string;  // For department-level events
  year?: number;
  month?: number;
  data: ForecastEventData;
  uiContext?: ForecastEventUiContext;
}

export interface DeleteEventParams {
  id: string;
  orgId: number;
}

// ============ Response Types ============

export interface GetEventsResponse {
  events: ForecastEvent[];
  count: number;
  limit: number;
  offset: number;
}

// ============ API Functions ============

/**
 * Get events for an account or location
 */
export async function getEvents(params: GetEventsParams): Promise<GetEventsResponse> {
  const queryParams = new URLSearchParams();

  queryParams.set('orgId', String(params.orgId));
  if (params.locationId !== undefined) queryParams.set('locationId', String(params.locationId));
  if (params.versionId !== undefined) queryParams.set('versionId', String(params.versionId));
  if (params.accountId !== undefined) queryParams.set('accountId', String(params.accountId));
  if (params.departmentName !== undefined) queryParams.set('departmentName', params.departmentName);
  if (params.year !== undefined) queryParams.set('year', String(params.year));
  if (params.month !== undefined) queryParams.set('month', String(params.month));
  if (params.types && params.types.length > 0) queryParams.set('types', params.types.join(','));
  if (params.limit !== undefined) queryParams.set('limit', String(params.limit));
  if (params.offset !== undefined) queryParams.set('offset', String(params.offset));

  const url = `${API_ENDPOINTS.FORECAST_V3_EVENTS}?${queryParams.toString()}`;
  const response = await api.get(url);
  return response.data;
}

/**
 * Create a new event or note
 */
export async function createEvent(params: CreateEventParams): Promise<ForecastEvent> {
  const response = await api.post(API_ENDPOINTS.FORECAST_V3_EVENTS, params);
  return response.data;
}

/**
 * Create a manual note (for account or department)
 */
export async function createNote(params: {
  orgId: number;
  locationId: number;
  versionId: number;
  accountId?: number;
  departmentName?: string;  // For department-level notes
  accountType?: 'Revenue' | 'Expense';  // Required with departmentName
  year?: number;
  month?: number;
  content: string;
  uiContext?: ForecastEventUiContext;
}): Promise<ForecastEvent> {
  return createEvent({
    type: 'NOTE',
    orgId: params.orgId,
    locationId: params.locationId,
    versionId: params.versionId,
    accountId: params.accountId,
    departmentName: params.departmentName,
    year: params.year,
    month: params.month,
    data: {
      content: params.content,
      accountType: params.accountType,
    },
    uiContext: params.uiContext,
  });
}

/**
 * Soft delete an event
 */
export async function deleteEvent(params: DeleteEventParams): Promise<{ success: boolean; message: string }> {
  const url = `${API_ENDPOINTS.FORECAST_V3_EVENTS}?id=${params.id}&orgId=${params.orgId}`;
  const response = await api.delete(url);
  return response.data;
}

// ============ Helper Functions ============

/**
 * Format event type for display
 */
export function formatEventType(type: ForecastEventType): string {
  switch (type) {
    case 'DRIVER_CHANGED':
      return 'Driver Changed';
    case 'OVERRIDE_CREATED':
      return 'Override Created';
    case 'OVERRIDE_UPDATED':
      return 'Override Updated';
    case 'OVERRIDE_CLEARED':
      return 'Override Cleared';
    case 'ADJUSTMENT_CREATED':
      return 'Adjustment Created';
    case 'ADJUSTMENT_UPDATED':
      return 'Adjustment Updated';
    case 'ADJUSTMENT_DELETED':
      return 'Adjustment Deleted';
    case 'DAILY_VALUES_CREATED':
      return 'Daily Values Created';
    case 'DAILY_VALUES_UPDATED':
      return 'Daily Values Updated';
    case 'DAILY_VALUES_CLEARED':
      return 'Daily Values Cleared';
    case 'DAILY_VALUES_SPREAD':
      return 'Daily Values Spread';
    case 'DEPARTMENT_DRIVER_SET':
      return 'Department Driver Set';
    case 'DEPARTMENT_DRIVER_UPDATED':
      return 'Department Driver Updated';
    case 'DEPARTMENT_DRIVER_CLEARED':
      return 'Department Driver Cleared';
    case 'NOTE':
      return 'Note';
    default:
      return type;
  }
}

/**
 * Check if an event type is auto-generated (vs manual note)
 */
export function isAutoEvent(type: ForecastEventType): boolean {
  return type !== 'NOTE';
}

/**
 * Get icon name for event type (for use with Lucide icons)
 */
export function getEventIcon(type: ForecastEventType): string {
  switch (type) {
    case 'DRIVER_CHANGED':
      return 'Calculator';
    case 'OVERRIDE_CREATED':
    case 'OVERRIDE_UPDATED':
      return 'Edit3';
    case 'OVERRIDE_CLEARED':
      return 'Eraser';
    case 'ADJUSTMENT_CREATED':
    case 'ADJUSTMENT_UPDATED':
      return 'SlidersHorizontal';
    case 'ADJUSTMENT_DELETED':
      return 'Trash2';
    case 'DAILY_VALUES_CREATED':
    case 'DAILY_VALUES_UPDATED':
      return 'Calendar';
    case 'DAILY_VALUES_CLEARED':
      return 'CalendarX';
    case 'DAILY_VALUES_SPREAD':
      return 'BarChart3';
    case 'DEPARTMENT_DRIVER_SET':
    case 'DEPARTMENT_DRIVER_UPDATED':
      return 'FolderCog';
    case 'DEPARTMENT_DRIVER_CLEARED':
      return 'FolderX';
    case 'NOTE':
      return 'MessageSquare';
    default:
      return 'Circle';
  }
}

/**
 * Format the event data for display
 */
export function formatEventDescription(event: ForecastEvent): string {
  const { type, data } = event;

  switch (type) {
    case 'DRIVER_CHANGED': {
      const from = data.previousDriver || 'None';
      const to = data.newDriver || 'None';

      // If only the input changed (same driver type)
      if (from === to && data.previousInput !== data.newInput) {
        const prevInput = data.previousInput ?? 'N/A';
        const newInput = data.newInput ?? 'N/A';
        return `Updated ${to} input: ${prevInput} → ${newInput}`;
      }

      // Driver type changed
      if (from !== to) {
        const inputStr = data.newInput !== null && data.newInput !== undefined
          ? ` (${data.newInput})`
          : '';
        return `Changed driver: ${from} → ${to}${inputStr}`;
      }

      return `Updated driver: ${to}`;
    }

    case 'OVERRIDE_CREATED':
      return `Created override: $${data.newAmount?.toLocaleString() ?? '0'}`;

    case 'OVERRIDE_UPDATED': {
      const from = data.previousAmount?.toLocaleString() ?? '0';
      const to = data.newAmount?.toLocaleString() ?? '0';
      return `Updated override: $${from} → $${to}`;
    }

    case 'OVERRIDE_CLEARED':
      return `Cleared override (was $${data.previousAmount?.toLocaleString() ?? '0'})`;

    case 'ADJUSTMENT_CREATED': {
      const typeStr = data.adjustmentType === 'PERCENT' ? '%' : '$';
      const value = data.adjustmentType === 'PERCENT'
        ? `${data.adjustmentValue}%`
        : `$${data.adjustmentValue?.toLocaleString()}`;
      return `Created ${data.adjustmentType?.toLowerCase()} adjustment: ${value}`;
    }

    case 'ADJUSTMENT_UPDATED': {
      const newValue = data.adjustmentType === 'PERCENT'
        ? `${data.adjustmentValue}%`
        : `$${data.adjustmentValue?.toLocaleString()}`;

      // Show "from X → Y" if we have previous values
      if (data.previousValue !== undefined) {
        const prevValue = data.previousType === 'PERCENT'
          ? `${data.previousValue}%`
          : `$${data.previousValue?.toLocaleString()}`;
        return `Updated adjustment: ${prevValue} → ${newValue}`;
      }

      return `Updated adjustment to ${newValue}`;
    }

    case 'ADJUSTMENT_DELETED':
      return `Deleted adjustment${data.description ? `: ${data.description}` : ''}`;

    case 'DAILY_VALUES_CREATED':
      return `Created daily values: ${data.dailyValuesCount || 0} days, $${data.dailyValuesTotal?.toLocaleString() ?? '0'} total`;

    case 'DAILY_VALUES_UPDATED': {
      const countStr = `${data.dailyValuesCount || 0} days`;
      const totalStr = `$${data.dailyValuesTotal?.toLocaleString() ?? '0'}`;
      if (data.previousDailyTotal !== undefined && data.previousDailyTotal !== null) {
        const prevStr = `$${data.previousDailyTotal.toLocaleString()}`;
        return `Updated daily values: ${countStr}, ${prevStr} → ${totalStr}`;
      }
      return `Updated daily values: ${countStr}, ${totalStr} total`;
    }

    case 'DAILY_VALUES_CLEARED':
      return `Cleared daily values (was $${data.previousDailyTotal?.toLocaleString() ?? '0'})`;

    case 'DAILY_VALUES_SPREAD': {
      const patternName = {
        even: 'Even',
        weekend: 'Weekend',
        ly: 'Last Year',
        otb: 'OTB',
        manual: 'Manual',
      }[data.spreadPattern || 'manual'];
      return `Spread using ${patternName} pattern: $${data.dailyValuesTotal?.toLocaleString() ?? '0'}`;
    }

    case 'DEPARTMENT_DRIVER_SET': {
      // departmentName is at top level of event, not in data
      const dept = event.departmentName || data.departmentName || 'Unknown';
      const driver = data.newDriver || 'None';
      const affected = data.affectedAccounts ?? 0;
      return `Set ${dept} department driver to ${driver}${affected > 0 ? ` (${affected} accounts affected)` : ''}`;
    }

    case 'DEPARTMENT_DRIVER_UPDATED': {
      // departmentName is at top level of event, not in data
      const dept = event.departmentName || data.departmentName || 'Unknown';
      const from = data.previousDriver || 'None';
      const to = data.newDriver || 'None';
      const affected = data.affectedAccounts ?? 0;
      return `Changed ${dept} department driver: ${from} → ${to}${affected > 0 ? ` (${affected} accounts affected)` : ''}`;
    }

    case 'DEPARTMENT_DRIVER_CLEARED': {
      // departmentName is at top level of event, not in data
      const dept = event.departmentName || data.departmentName || 'Unknown';
      const prev = data.previousDriver || 'None';
      return `Cleared ${dept} department driver (was ${prev})`;
    }

    case 'NOTE':
      return data.content || '';

    default:
      return '';
  }
}
